"use client"

import { useEffect, useRef, useState } from "react"
import { MapPin, Phone, Mail, Clock, Twitter, Instagram, Youtube, Train, Send } from 'lucide-react'

const ContactDetailItem = ({ icon: Icon, title, description }) => (
  <div className="group relative">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="relative flex items-start gap-4 p-5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all duration-500">
      <div className="flex-shrink-0 w-11 h-11 bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-xl border border-white/10 rounded-xl flex items-center justify-center">
        <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <h3 className="text-xs font-medium text-white/60 mb-1 tracking-wide">{title}</h3>
        <p className="text-sm text-white font-light leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
)

const SocialButton = ({ icon: Icon, href, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="group relative flex items-center justify-center gap-2 px-6 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl text-white font-light hover:bg-white/10 hover:border-white/20 transition-all duration-500"
  >
    <Icon className="w-4 h-4" strokeWidth={1.5} />
    <span className="text-sm">{label}</span>
  </a>
)

const OfficeCard = ({ city, address, postalCode }) => (
  <div className="relative group">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="relative p-6 bg-white/5 backdrop-blur-xl border border-white/10 border-l-2 border-l-blue-500 rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all duration-500">
      <h3 className="text-xl font-light text-white mb-3">{city}</h3>
      <p className="text-white/60 font-light text-sm leading-relaxed">
        {address}<br />
        {postalCode}
      </p>
    </div>
  </div>
)

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    console.log('Form submitted:', formData)
  }

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="firstName" className="block text-sm font-light text-white/60 mb-2">
            First name
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:bg-white/10 focus:border-blue-500/50 transition-all duration-300 font-light"
            placeholder=""
          />
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-light text-white/60 mb-2">
            Last name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:bg-white/10 focus:border-blue-500/50 transition-all duration-300 font-light"
            placeholder=""
          />
        </div>
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-light text-white/60 mb-2">
          Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:bg-white/10 focus:border-blue-500/50 transition-all duration-300 font-light"
          placeholder=""
        />
      </div>
      <div>
        <label htmlFor="subject" className="block text-sm font-light text-white/60 mb-2">
          Subject
        </label>
        <input
          type="text"
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:bg-white/10 focus:border-blue-500/50 transition-all duration-300 font-light"
          placeholder=""
        />
      </div>
      <div>
        <label htmlFor="message" className="block text-sm font-light text-white/60 mb-2">
          Message
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          className="w-full px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:bg-white/10 focus:border-blue-500/50 transition-all duration-300 resize-none font-light"
          placeholder=""
        />
      </div>
      <button
        type="button"
        onClick={handleSubmit}
        className="group relative w-full overflow-hidden rounded-xl"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="relative flex items-center justify-center gap-2 px-6 py-3.5 text-white font-normal text-base">
          Let's talk
        </div>
      </button>
      <p className="text-xs text-white/40 font-light">
        By submitting this form, I agree to the{' '}
        <a href="#" className="text-blue-400 hover:text-blue-300 transition-colors">
          privacy policy
        </a>
        .
      </p>
    </div>
  )
}

const TestimonialCard = () => (
  <div className="relative group">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
    <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
      <div className="flex items-start gap-4 mb-6">
       
        <h3 className="text-2xl font-light text-blue-400 pt-2">Aeronauts NSUT</h3>
      </div>
      
      <blockquote className="text-white/90 font-light text-base leading-relaxed mb-6 italic">
        "Pioneering the future of drone technology through innovation, precision engineering, and sustainable solutions. We transform ideas into reality."
      </blockquote>
      
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-full flex items-center justify-center">
          <span className="text-white font-light text-lg">AN</span>
        </div>
        <div>
          <p className="text-white font-normal text-sm">Aeronauts Team</p>
          <p className="text-white/50 font-light text-xs">Innovation Leaders</p>
        </div>
      </div>
    </div>
  </div>
)

export default function ContactUs() {
  const mapRef = useRef(null)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    const googleMapScript = document.createElement("script")
    googleMapScript.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAPMo7u_To14JCMRl4n-WERR1AfslABSDs&callback=initMap&libraries=places&v=weekly`
    googleMapScript.async = true
    googleMapScript.defer = true
    window.document.body.appendChild(googleMapScript)

    window.initMap = () => {
      const geetaColony = { lat: 28.65440172054759, lng: 77.26798546545048 }
      const map = new window.google.maps.Map(mapRef.current, {
        center: geetaColony,
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#8b92a8" }] },
          {
            featureType: "administrative.locality",
            elementType: "labels.text.fill",
            stylers: [{ color: "#c5cae9" }],
          },
          {
            featureType: "poi",
            elementType: "labels.text.fill",
            stylers: [{ color: "#8b92a8" }],
          },
          {
            featureType: "poi.park",
            elementType: "geometry",
            stylers: [{ color: "#2c3e50" }],
          },
          {
            featureType: "road",
            elementType: "geometry",
            stylers: [{ color: "#2c3442" }],
          },
          {
            featureType: "road",
            elementType: "geometry.stroke",
            stylers: [{ color: "#212a37" }],
          },
          {
            featureType: "road.highway",
            elementType: "geometry",
            stylers: [{ color: "#3c4858" }],
          },
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: "#0f1419" }],
          },
        ],
      })
      new window.google.maps.Marker({
        position: geetaColony,
        map: map,
        title: "Aeronauts NSUT",
        icon: {
          url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
        },
      })
    }

    setIsLoaded(true)

    return () => {
      if (window.document.body.contains(googleMapScript)) {
        window.document.body.removeChild(googleMapScript)
      }
      delete window.initMap
    }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-20">
        {/* Header */}
        <div className="mb-16 space-y-4">
          <h1 className="text-5xl md:text-6xl font-light text-white tracking-tight">
            Let's talk about your project
          </h1>
          <p className="text-lg text-white/60 font-light max-w-2xl leading-relaxed">
            We help companies and individuals build out their brand guidelines.
          </p>
        </div>

        {/* Main Split Layout - Form & Testimonial */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {/* LEFT SIDE - Contact Form */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-3xl blur-2xl" />
            <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
              <ContactForm />
            </div>
          </div>

          {/* RIGHT SIDE - Testimonial */}
          <div className="flex flex-col justify-center">
            <TestimonialCard />
          </div>
        </div>

        {/* Our Offices Section */}
        <div className="mb-20">
          <div className="mb-8">
            <h2 className="text-4xl font-light text-white mb-4">Our offices</h2>
            <p className="text-white/60 font-light leading-relaxed max-w-2xl">
              Visit us at our main campus location. We're conveniently accessible via metro and ready to collaborate on your next project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <OfficeCard 
              city="Delhi Campus"
              address="NSUT East Campus, Geeta Colony"
              postalCode="Delhi 110031"
            />
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-6 bg-white/5 backdrop-blur-xl border border-white/10 border-l-2 border-l-purple-500 rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all duration-500 h-full flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-3">
                  <Train className="w-5 h-5 text-purple-400" strokeWidth={1.5} />
                  <h3 className="text-lg font-light text-white">Nearest Metro</h3>
                </div>
                <p className="text-white/60 font-light text-sm">
                  Shastri Park & Laxmi Nagar
                </p>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-6 bg-white/5 backdrop-blur-xl border border-white/10 border-l-2 border-l-blue-500 rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all duration-500 h-full flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-3">
                  <Phone className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                  <h3 className="text-lg font-light text-white">Call Us</h3>
                </div>
                <p className="text-white/60 font-light text-sm">
                  +91 7903803472
                </p>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative p-6 bg-white/5 backdrop-blur-xl border border-white/10 border-l-2 border-l-green-500 rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all duration-500 h-full flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="w-5 h-5 text-green-400" strokeWidth={1.5} />
                  <h3 className="text-lg font-light text-white">Email</h3>
                </div>
                <p className="text-white/60 font-light text-sm break-all">
                  aeronauts.nsut@gmail.com
                </p>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden h-96">
              <div ref={mapRef} className="w-full h-full" />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="text-center space-y-8">
          <h2 className="text-2xl font-light text-white">Connect With Us</h2>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <SocialButton 
              href="https://www.linkedin.com/company/aeronauts-nsut" 
              label="LinkedIn" 
              icon={Twitter}
            />
            <SocialButton 
              href="https://www.instagram.com/aeronauts.nsut" 
              label="Instagram" 
              icon={Instagram}
            />
            <SocialButton 
              href="https://www.youtube.com/@git_alien" 
              label="YouTube" 
              icon={Youtube}
            />
          </div>
        </div>
      </div>
    </div>
  )
}